import type { FactId } from "../model.ts";
import {
  isCleanDirectRetrieval,
  isIndependentEvidence,
  isValidDirectRetrieval,
  type Attempt,
} from "../responses/attempt.ts";

export const FACT_MASTERY_STAGES = [
  "UNSEEN",
  "SUPPORTED",
  "INDEPENDENT",
  "AUTOMATING",
  "RETRIEVAL_READY",
  "MASTERED",
] as const;
export type FactMasteryStage = (typeof FACT_MASTERY_STAGES)[number];

export const RETENTION_STATES = [
  "NOT_DUE",
  "DUE",
  "STABLE",
  "REFRESH_DUE",
] as const;
export type RetentionState = (typeof RETENTION_STATES)[number];

export interface FactMasteryRules {
  readonly version: string;
  readonly independentCorrectRequired: number;
  readonly independentLatestCorrectRequired: number;
  readonly automatingCorrectRequired: number;
  readonly automatingSessionCountRequired: number;
  readonly retrievalWindowSize: number;
  readonly retrievalCorrectRequired: number;
  readonly retrievalSessionCountRequired: number;
  readonly masteredCleanSuccessesRequired: number;
  readonly masteredSessionCountRequired: number;
  readonly masteredLatestCorrectRequired: number;
  readonly retentionGapMs: number;
  readonly maintenanceDueMs: number;
  readonly repeatedFailureCount: number;
}

export const DEFAULT_FACT_MASTERY_RULES: FactMasteryRules = {
  version: "fact-mastery-v1",
  independentCorrectRequired: 3,
  independentLatestCorrectRequired: 2,
  automatingCorrectRequired: 4,
  automatingSessionCountRequired: 2,
  retrievalWindowSize: 5,
  retrievalCorrectRequired: 4,
  retrievalSessionCountRequired: 2,
  masteredCleanSuccessesRequired: 5,
  masteredSessionCountRequired: 3,
  masteredLatestCorrectRequired: 4,
  retentionGapMs: 24 * 60 * 60 * 1000,
  maintenanceDueMs: 7 * 24 * 60 * 60 * 1000,
  repeatedFailureCount: 2,
};

export interface FactMastery {
  readonly factId: FactId;
  readonly currentStage: FactMasteryStage;
  readonly highestStageEver: FactMasteryStage;
  readonly retentionState: RetentionState;
  readonly refreshDue: boolean;
  readonly attemptCount: number;
  readonly independentSuccessCount: number;
  readonly cleanRetrievalSuccessCount: number;
  readonly sessionCount: number;
  readonly lastSeenAt: string | null;
  readonly lastSuccessAt: string | null;
  readonly masteredAt: string | null;
  readonly rulesetVersion: string;
}

function ordered(attempts: readonly Attempt[], factId: FactId): Attempt[] {
  return attempts
    .filter((attempt) => attempt.factId === factId)
    .sort((a, b) => Date.parse(a.submittedAt) - Date.parse(b.submittedAt));
}

function distinctSessions(attempts: readonly Attempt[]): number {
  return new Set(attempts.map((attempt) => attempt.sessionId)).size;
}

function hasRetentionGap(attempts: readonly Attempt[], requiredGapMs: number): boolean {
  for (let index = 1; index < attempts.length; index += 1) {
    const previous = Date.parse(attempts[index - 1].submittedAt);
    const current = Date.parse(attempts[index].submittedAt);
    if (current - previous >= requiredGapMs) return true;
  }
  return false;
}

function stageRank(stage: FactMasteryStage): number {
  return FACT_MASTERY_STAGES.indexOf(stage);
}

function maxStage(a: FactMasteryStage, b: FactMasteryStage): FactMasteryStage {
  return stageRank(a) >= stageRank(b) ? a : b;
}

function rawStage(
  attempts: readonly Attempt[],
  rules: FactMasteryRules,
): FactMasteryStage {
  if (attempts.length === 0) return "UNSEEN";

  let stage: FactMasteryStage = attempts.some((attempt) => attempt.correct)
    ? "SUPPORTED"
    : "UNSEEN";

  const independentSuccesses = attempts.filter(isIndependentEvidence);
  const independentTail = attempts.slice(-rules.independentLatestCorrectRequired);
  if (
    independentSuccesses.length >= rules.independentCorrectRequired
    && independentTail.length >= rules.independentLatestCorrectRequired
    && independentTail.every(isIndependentEvidence)
  ) {
    stage = "INDEPENDENT";
  }

  const automatingTail = attempts.slice(-rules.independentCorrectRequired);
  if (
    independentSuccesses.length >= rules.automatingCorrectRequired
    && distinctSessions(independentSuccesses) >= rules.automatingSessionCountRequired
    && automatingTail.length >= rules.independentCorrectRequired
    && automatingTail.every(isIndependentEvidence)
  ) {
    stage = "AUTOMATING";
  }

  const retrievalAttempts = attempts.filter(
    (attempt) => attempt.responseMode === "DIRECT_RETRIEVAL",
  );
  const retrievalWindow = retrievalAttempts.slice(-rules.retrievalWindowSize);
  const validRetrievals = retrievalWindow.filter(isValidDirectRetrieval);
  if (
    validRetrievals.length >= rules.retrievalCorrectRequired
    && distinctSessions(validRetrievals) >= rules.retrievalSessionCountRequired
  ) {
    stage = "RETRIEVAL_READY";
  }

  const cleanRetrievals = retrievalAttempts.filter(isCleanDirectRetrieval);
  const masteredTail = retrievalAttempts.slice(-rules.masteredLatestCorrectRequired);
  if (
    cleanRetrievals.length >= rules.masteredCleanSuccessesRequired
    && distinctSessions(cleanRetrievals) >= rules.masteredSessionCountRequired
    && masteredTail.length >= rules.masteredLatestCorrectRequired
    && masteredTail.every(isCleanDirectRetrieval)
    && hasRetentionGap(cleanRetrievals, rules.retentionGapMs)
  ) {
    stage = "MASTERED";
  }

  return stage;
}

export function evaluateFactMastery(
  factId: FactId,
  allAttempts: readonly Attempt[],
  now: string,
  rules: FactMasteryRules = DEFAULT_FACT_MASTERY_RULES,
): FactMastery {
  const attempts = ordered(allAttempts, factId);
  let highestStageEver: FactMasteryStage = "UNSEEN";
  let masteredAt: string | null = null;

  for (let index = 1; index <= attempts.length; index += 1) {
    const stage = rawStage(attempts.slice(0, index), rules);
    highestStageEver = maxStage(highestStageEver, stage);
    if (stage === "MASTERED" && masteredAt === null) {
      masteredAt = attempts[index - 1].submittedAt;
    }
  }

  const calculatedStage = rawStage(attempts, rules);
  let currentStage = calculatedStage;
  let refreshDue = false;

  if (highestStageEver === "MASTERED" && calculatedStage !== "MASTERED") {
    const afterMastery = attempts.filter(
      (attempt) => masteredAt !== null && Date.parse(attempt.submittedAt) > Date.parse(masteredAt),
    );
    const recentRetrievals = afterMastery
      .filter((attempt) => attempt.responseMode === "DIRECT_RETRIEVAL")
      .slice(-rules.masteredLatestCorrectRequired);
    const failures = recentRetrievals.filter((attempt) => !isValidDirectRetrieval(attempt)).length;

    refreshDue = failures > 0;
    if (failures > 0 && failures < rules.repeatedFailureCount) {
      currentStage = "MASTERED";
    } else if (failures >= rules.repeatedFailureCount) {
      currentStage = maxStage(calculatedStage, "AUTOMATING");
    }
  }

  const lastSeen = attempts.at(-1) ?? null;
  const lastSuccess = [...attempts].reverse().find((attempt) => attempt.correct) ?? null;
  let retentionState: RetentionState = "NOT_DUE";

  if (refreshDue) {
    retentionState = "REFRESH_DUE";
  } else if (highestStageEver === "MASTERED" && lastSuccess) {
    retentionState = Date.parse(now) - Date.parse(lastSuccess.submittedAt) >= rules.maintenanceDueMs
      ? "DUE"
      : "STABLE";
  }

  const independentSuccesses = attempts.filter(isIndependentEvidence);
  const cleanRetrievals = attempts.filter(isCleanDirectRetrieval);

  return {
    factId,
    currentStage,
    highestStageEver,
    retentionState,
    refreshDue,
    attemptCount: attempts.length,
    independentSuccessCount: independentSuccesses.length,
    cleanRetrievalSuccessCount: cleanRetrievals.length,
    sessionCount: distinctSessions(attempts),
    lastSeenAt: lastSeen?.submittedAt ?? null,
    lastSuccessAt: lastSuccess?.submittedAt ?? null,
    masteredAt,
    rulesetVersion: rules.version,
  };
}
